import React, { useDeferredValue } from "react"
import upgradeBannerIllustration from "~/images/upgrade_illustration.svg"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { Snackbar, SnackbarText, SnackbarLink } from "~/react/ui/snackbar"
import { Icon } from "~/react/ui/icon"
import {
  PillTabs,
  PillTabsList,
  PillTabsTriggerName,
  PillTabsTriggerHint,
  PillTabsTrigger,
  PillTabsContent,
} from "~/react/ui/pill-tabs"
import { TooltipProvider } from "~/react/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuDivider,
  DropdownMenuHint,
} from "~/react/ui/dropdown-menu"
import {
  Filters,
  sortFunction,
  filterFunction,
  clearFilters,
  type FiltersType,
} from "~/react/components/search/filters"
import { LeadSaveManager } from "~/react/components/lead-save-manager"
import { VirtualizedResults, ESTIMATED_RESULT_SIZE } from "~/react/components/multi_domain_search/virtualized-results"
import {
  leads_path,
  multi_domain_search_search_index_path,
  multi_domain_search_emails_search_index_path,
  edit_subscriptions_path,
} from "~/lib/routes"
import { useAsyncQueuer } from "@tanstack/react-pacer"
import { MultiDomainSearchDomain } from "~/types/serializers"
import { useCampaignsSave } from "~/react/hooks/useCampaignsSave"
import { useJobTitleSuggestions } from "~/react/hooks/useJobTitleSuggestions"
import { useSplittedResults } from "@/react/hooks/useSplittedResults"
import { useLocationMappings } from "@/react/hooks/useLocationMappings"
import { type TabSelected } from "~/react/types/tabSelected"
import { LoadingSpinner } from "@/react/ui/loading-spinner"
import { useRevealCost } from "@/react/hooks/useRevealCost"
import { useEmailSearch } from "@/react/hooks/useEmailSearch"
import { useLeadsListSave } from "../hooks/useLeadsListSave"
import { Alert, AlertIcon, AlertContent, AlertTitle, AlertDescription } from "../ui/alert"
import { SheetAlert } from "../ui/sheet"

// Using shared constant from VirtualizedResults
const estimatedSize = ESTIMATED_RESULT_SIZE

interface MultiDomainSearchProps {
  numberOfCompanies: number
  numberOfResults: number
  genericEmailsCount: number
  personalEmailsCount: number
  allowDiscoverPaging: boolean
  filterParams: Record<string, any>
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  type: "companies" | "domains"
  numberOfCreditsLeft: number
  source: "discover" | "companies" | "search"
  clickToCopy?: boolean
}

const EmptyState = ({ clearFilters }: { clearFilters?: () => void }) => {
  return (
    <div className="d-flex flex-column gap-2 justify-content-center align-items-center mt-16">
      <span className="far fa-info-circle text-hint" aria-hidden="true"></span>
      <span className="text-body-secondary">{i18n.t("js.multi_domain_search.empty_state.text")}</span>

      {clearFilters && (
        <Button variant="linkGrey" className="fw-medium" onClick={clearFilters}>
          {i18n.t("js.multi_domain_search.empty_state.clear_filters")}
        </Button>
      )}
    </div>
  )
}

export default function MultiDomainSearch({
  numberOfCompanies,
  numberOfResults,
  genericEmailsCount,
  personalEmailsCount,
  allowDiscoverPaging,
  filterParams,
  numberOfCreditsLeft,
  leadsLists: initialLeadsLists,
  campaigns: initialCampaigns,
  type,
  source,
  clickToCopy = false,
}: MultiDomainSearchProps) {
  const [domains, setDomains] = React.useState<MultiDomainSearchDomain[]>([])
  const [nextSearchAfter, setNextSearchAfter] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [leadsLists, setLeadsLists] = React.useState<{ id: number; name: string }[]>(initialLeadsLists)
  const [campaigns, setCampaigns] = React.useState<{ id: number; name: string }[]>(initialCampaigns)
  const [tabSelected, setTabSelected] = React.useState<TabSelected>("all_people")
  const locationsMappings = useLocationMappings()
  const [filters, setFilters] = React.useState<FiltersType>({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: true,
    location: [],
  })

  const {
    results,
    setResults,
    revealingIds,
    revealedIds,
    revealedLeadsListId,
    operationId,
    lastRevealedIds,
    handleReveal,
    handleRevealMultiple,
    handleSelect,
    selectedIds,
  } = useEmailSearch({
    source,
    context: "multi_domain_search",
  })

  // Loading hook for fetching emails
  const fetchEmailsFn = async (emailIds: number[]) => {
    try {
      const response = await fetch(multi_domain_search_emails_search_index_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
        body: JSON.stringify({ email_ids: emailIds }),
      })

      if (!response.ok) {
        console.error(`Failed to fetch emails: ${response.status} ${response.statusText}`)
        return
      }

      const data = await response.json()

      if (!data.emails || !Array.isArray(data.emails)) {
        console.error("Invalid response format: expected emails array")
        return
      }

      setResults((prev) => {
        const existingIds = new Set(prev.map((result) => result.id))
        const newResults = data.emails.filter((email) => !existingIds.has(email.id))
        return [...prev, ...newResults]
      })
    } catch (error) {
      console.error("Error fetching emails:", error)
    } finally {
      if (queue.peekPendingItems().length === 0) {
        setIsLoading(false)
      }
    }
  }
  const queue = useAsyncQueuer(fetchEmailsFn, { concurrency: 10, wait: 10 })

  const fetchResults = async (domains?: string[]) => {
    setIsLoading(true)
    const batchSize = 10000

    const body = { search_after: nextSearchAfter }
    if (domains && domains.length > 0) {
      body.domain_params = { domains: domains.join("|") }
    } else if (type === "companies") {
      body.companies_params = filterParams
    } else {
      body.domain_params = filterParams
    }

    try {
      const response = await fetch(multi_domain_search_search_index_path(), {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
      })
      const data = await response.json()
      const emailIds = data.email_ids
      const domains = (data.domains as MultiDomainSearchDomain[]) || []
      setDomains((prev) => [...prev, ...domains])

      for (let i = 0; i < emailIds.length; i += batchSize) {
        queue.addItem(emailIds.slice(i, i + batchSize))
      }

      if (data.next_search_after !== null) {
        setNextSearchAfter(data.next_search_after)
      }
    } catch (err) {
      setError(err)
    }
  }
  React.useEffect(() => {
    fetchResults()
  }, [nextSearchAfter])

  const handleCampaignsSave = useCampaignsSave(results, campaigns, setCampaigns)
  const handleLeadsListSave = useLeadsListSave(results, setLeadsLists, setResults)

  // Defer results updates to keep filters responsive during data loading.
  // This allows React to prioritize user interactions (like filter changes)
  // while the heavy filtering computation runs in the background.
  const deferredResults = useDeferredValue(results)

  const allPeopleListRef = React.useRef(null)
  const decisionMakersListRef = React.useRef(null)
  const genericListRef = React.useRef(null)
  const tabsRef = React.useRef(null)
  const revealAllButtonRef = React.useRef(null)
  const [tabsContentHeight, setTabsContentHeight] = React.useState("100vh")

  const calculateTabsContentHeight = React.useCallback(() => {
    const tabsContentRef = allPeopleListRef.current || decisionMakersListRef.current || genericListRef.current

    if (!tabsContentRef) return

    const windowHeight = window.innerHeight
    const tabsContentElement = tabsContentRef as HTMLElement
    const tabsContentTop = tabsContentElement.getBoundingClientRect().top
    const footerElement = document.querySelector(".sidepanel__footer") as HTMLElement
    const footerHeight = footerElement ? footerElement.offsetHeight : 0

    // 2px is a random gap to ensure we don't have two scrollbars in the sidepanel
    setTabsContentHeight(`${Math.max(windowHeight - tabsContentTop - footerHeight - 2, 200)}px`)
  }, [])

  React.useEffect(() => {
    calculateTabsContentHeight()

    const handleResize = () => {
      calculateTabsContentHeight()
    }

    window.addEventListener("resize", handleResize)

    const timeoutId = setTimeout(calculateTabsContentHeight, 100)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(timeoutId)
    }
  }, [calculateTabsContentHeight, filters, results])

  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.hideAlreadySaved ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0 ||
    !!filters.peoplePerCompany

  const filteredResults = React.useMemo(() => {
    const baseFilteredResults = filterFunction(deferredResults, filters, locationsMappings, revealedIds)

    return baseFilteredResults.sort(sortFunction)
  }, [deferredResults, filters, locationsMappings, revealedIds])
  const { allPeopleResults, decisionMakersResults, genericResults } = useSplittedResults(filteredResults)

  // If there are no "All People" results but generic results exist,
  // automatically switch to the "generic" tab so it's shown by default.
  React.useEffect(() => {
    if (tabSelected === "all_people" && allPeopleResults.length === 0 && genericResults.length > 0) {
      setTabSelected("generic")
    }
  }, [tabSelected, allPeopleResults.length, genericResults.length])

  const jobTitles = useJobTitleSuggestions(deferredResults, filters, locationsMappings, revealedIds)
  // Also defer jobTitles to prevent Filters component from resetting local state
  // during data loading. The Filters component has a useEffect that resets all
  // local state when jobTitles changes, which would cause pending filter selections
  // to be lost.
  const deferredJobTitles = useDeferredValue(jobTitles)

  const allPeopleListVirtualizer = useVirtualizer({
    count: allPeopleResults.length,
    getScrollElement: () => allPeopleListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "all_people",
  })
  const decisionMakersListVirtualizer = useVirtualizer({
    count: decisionMakersResults.length,
    getScrollElement: () => decisionMakersListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "decision_makers",
  })
  const genericListVirtualizer = useVirtualizer({
    count: genericResults.length,
    getScrollElement: () => genericListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "generic",
  })

  const { revealAllOperationCost } = useRevealCost({
    selectedIds,
    tabSelected,
    results,
    allPeopleResults,
    decisionMakersResults,
    genericResults,
    numberOfCreditsLeft,
  })

  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    if (selectedIds.length > 0) {
      await handleRevealMultiple(selectedIds, { leads_list_id: listId })
    } else {
      await handleRevealMultiple(
        tabSelected === "all_people"
          ? allPeopleResults.map((e) => e.id)
          : tabSelected === "decision_makers"
            ? decisionMakersResults.map((e) => e.id)
            : genericResults.map((e) => e.id),
        { leads_list_id: listId },
      )
    }
  }

  React.useEffect(() => {
    if (revealAllButtonRef.current) {
      const tooltipInstance = window.bootstrap?.Tooltip?.getInstance(revealAllButtonRef.current)
      if (tooltipInstance) {
        tooltipInstance.setContent({
          ".tooltip-inner": i18n.t("js.multi_domain_search.footer.cost_tooltip", {
            count: selectedIds.length,
            cost: CurrencyHelper.numberWithDelimiter(revealAllOperationCost),
          }),
        })
      }
    }
  }, [revealAllOperationCost])

  // Calculate the already revealed count based on what will actually be revealed:
  // - When items are selected: count revealed items among selected items
  // - When no items are selected: count revealed items among all items in the current tab
  const alreadyRevealedCount = React.useMemo(() => {
    if (selectedIds.length > 0) {
      return selectedIds.filter((id) => results.find((result) => result.id === id)?.revealed).length
    }

    // When no selection, count revealed items in the current tab
    const currentTabResults =
      tabSelected === "all_people"
        ? allPeopleResults
        : tabSelected === "decision_makers"
          ? decisionMakersResults
          : genericResults

    return currentTabResults.filter((result) => result.revealed).length
  }, [selectedIds, results, tabSelected, allPeopleResults, decisionMakersResults, genericResults])

  const snackbarLinkParams: Record<string, number | null> = {}
  if (operationId) {
    snackbarLinkParams.operation_id = operationId
  }
  if (revealedLeadsListId) {
    snackbarLinkParams.leads_list_id = revealedLeadsListId
  }
  if (lastRevealedIds.length === 1) {
    const result = results.find((result) => lastRevealedIds.includes(result.id))

    if (result) {
      snackbarLinkParams.email = result.email
    }
  }

  return (
    <>
      <TooltipProvider>
        <header className="sidepanel__header mb-0">
          <h2 className="sidepanel__title">{i18n.t("js.multi_domain_search.header")}</h2>
        </header>

        {!allowDiscoverPaging && numberOfCompanies > 100 && (
          <SheetAlert>
            <Alert variant="info" size="small" className="py-6 px-8 gap-4 m-0 align-items-start">
              <AlertIcon>
                <img src={upgradeBannerIllustration} alt="" />
              </AlertIcon>
              <AlertContent>
                <AlertTitle className="fw-medium">
                  {i18n.t("js.multi_domain_search.upgrade_from_free.title", {
                    count: numberOfResults - results.length,
                  })}
                </AlertTitle>
                <AlertDescription>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: i18n.t("js.multi_domain_search.upgrade_from_free.description_html", {
                        count: numberOfResults - results.length,
                      }),
                    }}
                  />
                </AlertDescription>
                <Button size="sm" className="mt-4" asChild>
                  <a href={edit_subscriptions_path()} data-turbo-frame="_top">
                    <Icon icon="arrow-alt-circle-up" />
                    {i18n.t("js.multi_domain_search.upgrade_from_free.cta")}
                  </a>
                </Button>
              </AlertContent>
            </Alert>
          </SheetAlert>
        )}

        <Filters
          possibleFilters={[
            "department",
            "job_title",
            "verification_status",
            "required_field",
            "location",
            "already_saved",
            "people_per_company",
          ]}
          filters={filters}
          setFilters={(newFilters) => setFilters(newFilters)}
          jobTitles={deferredJobTitles}
          anyFiltersSet={anyFiltersSet}
          clearFilters={() => clearFilters(setFilters)}
        />

        <div className="sidepanel__body mt-4 pb-12">
          <PillTabs value={tabSelected} ref={tabsRef}>
            <PillTabsList size="md" fullWidth>
              <PillTabsTrigger value="all_people" onClick={() => setTabSelected("all_people")}>
                <PillTabsTriggerName>{i18n.t("js.multi_domain_search.tabs.all_people")}</PillTabsTriggerName>
                <PillTabsTriggerHint>
                  · {CurrencyHelper.numberWithDelimiter(allPeopleResults.length)}
                </PillTabsTriggerHint>
              </PillTabsTrigger>
              <PillTabsTrigger value="decision_makers" onClick={() => setTabSelected("decision_makers")}>
                <Icon icon="crown" size="sm" className="text-hint" />
                <PillTabsTriggerName>{i18n.t("js.multi_domain_search.tabs.decision_makers")}</PillTabsTriggerName>
                <PillTabsTriggerHint>
                  · {CurrencyHelper.numberWithDelimiter(decisionMakersResults.length)}
                </PillTabsTriggerHint>
              </PillTabsTrigger>
              <PillTabsTrigger value="generic" onClick={() => setTabSelected("generic")}>
                <PillTabsTriggerName>{i18n.t("js.multi_domain_search.tabs.generic")}</PillTabsTriggerName>
                <PillTabsTriggerHint>· {CurrencyHelper.numberWithDelimiter(genericResults.length)}</PillTabsTriggerHint>
              </PillTabsTrigger>
            </PillTabsList>

            <PillTabsContent
              value="all_people"
              ref={allPeopleListRef}
              style={{ height: tabsContentHeight, overflow: "auto" }}
            >
              {allPeopleResults.length > 0 || isLoading ? (
                <>
                  <VirtualizedResults
                    virtualItems={allPeopleListVirtualizer.getVirtualItems()}
                    getTotalSize={() => allPeopleListVirtualizer.getTotalSize()}
                    measureElement={allPeopleListVirtualizer.measureElement}
                    results={allPeopleResults}
                    domains={domains}
                    selectedIds={selectedIds}
                    leadsLists={leadsLists}
                    campaigns={campaigns}
                    onSelect={handleSelect}
                    onReveal={handleReveal}
                    onSaveEmail={handleLeadsListSave}
                    onSaveCampaign={handleCampaignsSave}
                    clickToCopy={clickToCopy}
                  />
                  {isLoading && <LoadingSpinner />}
                </>
              ) : anyFiltersSet ? (
                <EmptyState clearFilters={() => clearFilters(setFilters)} />
              ) : (
                <EmptyState />
              )}
            </PillTabsContent>

            <PillTabsContent
              value="decision_makers"
              ref={decisionMakersListRef}
              style={{ height: tabsContentHeight, overflow: "auto" }}
            >
              {decisionMakersResults.length > 0 || isLoading ? (
                <>
                  <VirtualizedResults
                    virtualItems={decisionMakersListVirtualizer.getVirtualItems()}
                    getTotalSize={() => decisionMakersListVirtualizer.getTotalSize()}
                    measureElement={decisionMakersListVirtualizer.measureElement}
                    results={decisionMakersResults}
                    domains={domains}
                    selectedIds={selectedIds}
                    leadsLists={leadsLists}
                    campaigns={campaigns}
                    onSelect={handleSelect}
                    onReveal={handleReveal}
                    onSaveEmail={handleLeadsListSave}
                    onSaveCampaign={handleCampaignsSave}
                    clickToCopy={clickToCopy}
                  />
                  {isLoading && <LoadingSpinner />}
                </>
              ) : anyFiltersSet ? (
                <EmptyState clearFilters={() => clearFilters(setFilters)} />
              ) : (
                <EmptyState />
              )}
            </PillTabsContent>

            <PillTabsContent
              value="generic"
              ref={genericListRef}
              style={{ height: tabsContentHeight, overflow: "auto" }}
            >
              {genericResults.length > 0 || isLoading ? (
                <>
                  <VirtualizedResults
                    virtualItems={genericListVirtualizer.getVirtualItems()}
                    getTotalSize={() => genericListVirtualizer.getTotalSize()}
                    measureElement={genericListVirtualizer.measureElement}
                    results={genericResults}
                    domains={domains}
                    selectedIds={selectedIds}
                    leadsLists={leadsLists}
                    campaigns={campaigns}
                    onSelect={handleSelect}
                    onReveal={handleReveal}
                    onSaveEmail={handleLeadsListSave}
                    onSaveCampaign={handleCampaignsSave}
                    clickToCopy={clickToCopy}
                  />
                  {isLoading && <LoadingSpinner />}
                </>
              ) : anyFiltersSet ? (
                <EmptyState clearFilters={() => clearFilters(setFilters)} />
              ) : (
                <EmptyState />
              )}
            </PillTabsContent>
          </PillTabs>
        </div>

        <footer className="sidepanel__footer sidepanel__footer--sticky">
          {lastRevealedIds.length > 0 && (
            <Snackbar>
              <Icon icon="check-circle" variant="solid" size="sm" className="text-success" />
              <SnackbarText>
                <strong className="fw-medium">
                  {i18n.t("js.multi_domain_search.footer.leads", {
                    count: lastRevealedIds.length,
                    pretty_count: CurrencyHelper.numberWithDelimiter(lastRevealedIds.length),
                  })}
                </strong>
                &nbsp;
                {revealedLeadsListId
                  ? i18n.t("js.multi_domain_search.footer.saved_in_list", {
                      listName: leadsLists.find((list) => list.id === revealedLeadsListId)?.name,
                    })
                  : i18n.t("js.multi_domain_search.footer.saved")}
              </SnackbarText>
              <SnackbarLink href={leads_path(snackbarLinkParams)} turboFrame="_top">
                {i18n.t("js.multi_domain_search.footer.see_in_leads")}
              </SnackbarLink>
            </Snackbar>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="primary" data-bs-toggle="dropdown" data-bs-auto-close="outside">
                {revealingIds.length > 0
                  ? i18n.t("js.multi_domain_search.footer.saving_leads")
                  : i18n.t("js.multi_domain_search.footer.save_leads", {
                      count: selectedIds.length,
                    })}
                <ButtonSuffix>
                  <span className="cost-indicator cost-indicator--bare">
                    <Icon icon="coin" size="xs" className="cost-indicator__icon" />
                    {CurrencyHelper.numberWithDelimiter(revealAllOperationCost)}
                  </span>
                </ButtonSuffix>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuHint>
                <Icon icon="coin" size="xs" className="cost-indicator__icon" />
                {i18n.t("js.multi_domain_search.footer.cost_tooltip", {
                  count: selectedIds.length,
                  cost: CurrencyHelper.numberWithDelimiter(revealAllOperationCost),
                })}
              </DropdownMenuHint>
              <DropdownMenuDivider />
              <DropdownMenuItem>
                <LeadSaveManager
                  className="w-100"
                  id="multi-domain-search"
                  leadsLists={leadsLists}
                  onSave={handleLeadManagerSave}
                  disableButtons={revealingIds.length > 0}
                  cost={revealAllOperationCost}
                  hint={
                    alreadyRevealedCount > 0
                      ? i18n.t("js.multi_domain_search.footer.existing_leads_hint", { count: alreadyRevealedCount })
                      : undefined
                  }
                  customPosition={(triggerElement) => {
                    const dropdownMenu = triggerElement.nextElementSibling as HTMLElement
                    if (!dropdownMenu?.classList.contains("dropdown-menu")) return

                    const parentDropdown = triggerElement.closest(".dropdown-menu") as HTMLElement
                    if (!parentDropdown) return

                    // Find all scrollable parents
                    const scrollableParents: HTMLElement[] = []
                    let element = parentDropdown.parentElement
                    while (element) {
                      const overflow = window.getComputedStyle(element).overflow
                      if (overflow === "auto" || overflow === "scroll" || overflow === "overlay") {
                        scrollableParents.push(element)
                      }
                      element = element.parentElement
                    }

                    const updatePosition = () => {
                      const parentRect = parentDropdown.getBoundingClientRect()

                      dropdownMenu.style.position = "fixed"
                      dropdownMenu.style.bottom = "0"
                      dropdownMenu.style.left = `${parentRect.width}px`
                      dropdownMenu.style.transform = "none"
                    }

                    // Initial position
                    updatePosition()

                    // Update on scroll and resize
                    window.addEventListener("scroll", updatePosition, true)
                    window.addEventListener("resize", updatePosition)
                    scrollableParents.forEach((parent) => {
                      parent.addEventListener("scroll", updatePosition)
                    })

                    // Cleanup when dropdown hides
                    const handleHide = () => {
                      window.removeEventListener("scroll", updatePosition, true)
                      window.removeEventListener("resize", updatePosition)
                      scrollableParents.forEach((parent) => {
                        parent.removeEventListener("scroll", updatePosition)
                      })
                      triggerElement.removeEventListener("hidden.bs.dropdown", handleHide)
                    }

                    triggerElement.addEventListener("hidden.bs.dropdown", handleHide)
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    {i18n.t("js.multi_domain_search.footer.save_in_a_specific_list")}
                    <Icon icon="angle-right" size="sm" aria-hidden="true" />
                  </div>
                </LeadSaveManager>
              </DropdownMenuItem>
              <li>
                <DropdownMenuItem asChild closeOnClick>
                  <button
                    ref={revealAllButtonRef}
                    disabled={revealingIds.length > 0}
                    onClick={() => {
                      if (selectedIds.length > 0) {
                        handleRevealMultiple(selectedIds)
                      } else {
                        handleRevealMultiple(
                          tabSelected === "all_people"
                            ? allPeopleResults.map((e) => e.id)
                            : tabSelected === "decision_makers"
                              ? decisionMakersResults.map((e) => e.id)
                              : genericResults.map((e) => e.id),
                        )
                      }
                    }}
                    className="dropdown-item"
                  >
                    {i18n.t("js.multi_domain_search.footer.save_in_leads")}
                  </button>
                </DropdownMenuItem>
              </li>
            </DropdownMenuContent>
          </DropdownMenu>
        </footer>
      </TooltipProvider>
    </>
  )
}
